import React from 'react';
import { View, ScrollView } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import getBestContrastColor from '../../../services/best-contrast-color';
import FilterDependencyMessage from '../FilterDependencyMessage';
import Badge from '../../Badge';

interface Props {
    variant?: 'radio' | 'checkbox';
    renderBadge?: boolean;
    label?: string;
    labels?: string[];
    textualSearch?: string;
    groupedLabels?: Record<string, string[]>;
}

export default function SelectedItemsList({ variant = 'radio', renderBadge, label, labels, textualSearch, groupedLabels }: Props) {
    const { styles, theme } = useStyles(styleSheet);

    const hasFlatLabels = !!labels && labels.length > 0;
    const hasGroupedLabels = !!groupedLabels && Object.values(groupedLabels).some(subLabels => subLabels.length > 0);

    if (!hasFlatLabels && !hasGroupedLabels) {
        return <FilterDependencyMessage message={translate('filterNotUsedForReport')} />;
    }

    const itemsCount =
        (hasGroupedLabels
            ? Object.values(groupedLabels ?? {}).reduce((acc, subLabels) => acc + subLabels.length, 0)
            : (labels?.length ?? 0))
        + (textualSearch ? 1 : 0);


    return (
        <View style={styles.container}>
            {label && <MyAppText style={styles.filterLabel}>{label}</MyAppText>}

            <View style={styles.countRow}>
                <MyAppText style={styles.countText}>
                    {translate('items')}: {itemsCount}
                </MyAppText>
            </View>
            {textualSearch &&
                <View style={{ flexDirection: 'column', gap: 8 }}>
                    <MyAppText style={styles.textualSearchLabel}>{translate('textualSearch')}</MyAppText>
                    <Badge mode='ROUNDED' size='medium'>{textualSearch}</Badge>
                </View>
            }
            {hasGroupedLabels ? (
                <ScrollView
                    style={styles.list}
                    contentContainerStyle={{ gap: 16 }}
                    nestedScrollEnabled
                >
                    {Object.entries(groupedLabels ?? {}).map(([category, subLabels]) => (
                        subLabels.length === 0 ? null : (
                            <View key={category}>
                                <View style={styles.row}>
                                    <View style={[styles.radio, styles.radioSelected]}>
                                        <View style={styles.radioInner} />
                                    </View>
                                    <MyAppText style={styles.categoryLabel}>{category}</MyAppText>
                                </View>

                                <View style={styles.subContainer}>
                                    {subLabels.map((sub) => (
                                        <View key={sub} style={styles.row}>
                                            <View style={[styles.checkbox, styles.checkboxSelected]}>
                                                <FontAwesomeIcon icon={faCheck as IconDefinition} fontSize={10} color={theme.colors.white['500']} />
                                            </View>
                                            {renderBadge ? (
                                                <View style={[styles.badge, { backgroundColor: theme.colors.neutral['300'] }]}>
                                                    <MyAppText style={styles.badgeText(theme.colors.neutral['300'])}>{sub}</MyAppText>
                                                </View>
                                            ) : (
                                                <MyAppText style={styles.label}>{sub}</MyAppText>
                                            )}
                                        </View>
                                    ))}
                                </View>
                            </View>
                        )
                    ))}
                </ScrollView>
            ) : (
                <ScrollView
                    style={styles.list}
                    contentContainerStyle={{ gap: 8 }}
                    nestedScrollEnabled
                >
                    {(labels ?? []).map((item) => (
                        <View key={String(item)} style={styles.row}>
                            {variant === 'checkbox' ? (
                                <View style={[styles.checkbox, styles.checkboxSelected]}>
                                    <FontAwesomeIcon icon={faCheck as IconDefinition} fontSize={10} color={theme.colors.white['500']} />
                                </View>
                            ) : (
                                <View style={[styles.radio, styles.radioSelected]}>
                                    <View style={styles.radioInner} />
                                </View>
                            )}
                            {renderBadge ? (
                                <View style={[styles.badge, { backgroundColor: theme.colors.neutral['300'] }]}>
                                    <MyAppText style={styles.badgeText(theme.colors.neutral['300'])}>{item}</MyAppText>
                                </View>
                            ) : (
                                <MyAppText style={styles.label}>{item}</MyAppText>
                            )}
                        </View>
                    ))}
                </ScrollView>
            )}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: { gap: 12 },
    filterLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
    countRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    countText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.labelColor,
    },
    loadingContainer: {
        paddingVertical: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    list: {
        maxHeight: 208,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    radio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    radioSelected: {
        borderColor: theme.colors.primary['500'],
    },
    radioInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.primary['500'],
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxSelected: {
        backgroundColor: theme.colors.primary['500'],
        borderColor: theme.colors.primary['500'],
    },
    badge: {
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 20,
    },
    badgeText: (tagColor: string) => ({
        color: getBestContrastColor(tagColor),
        fontSize: 14,
        fontWeight: '600',
    }),
    label: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    categoryLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
    subContainer: {
        paddingLeft: 34,
        paddingTop: 8,
        gap: 8,
    },
    emptyText: {
        fontSize: 14,
        color: theme.colors.neutral['300'],
        paddingVertical: 12,
    },
    textualSearchLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
}));
